import React, { useId, useRef, useState } from 'react';
import ClientEmailAutocompleteInput from '@/components/ClientEmailAutocompleteInput';
import { parseEmailListInput } from './quotationEmailRecipients';

type Props = {
  value: string;
  onChange: (value: string) => void;
  inputStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
};

export default function QuotationCcInput({ value, onChange, inputStyle, labelStyle }: Props) {
  const id = useId();
  const container = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');
  const emails = parseEmailListInput(value);
  const add = () => {
    const tokens = draft.split(/[,;\n]+/).map(item => item.trim()).filter(Boolean);
    if (!tokens.length || tokens.some(email => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      setError('Enter a valid email address before adding it.');
      return;
    }
    onChange(parseEmailListInput([...emails, draft]).join(', '));
    setDraft('');
    setError('');
    container.current?.querySelector('input')?.focus();
  };

  return (
    <div ref={container} style={{ display: 'grid', gap: 6, flex: '1 1 280px', maxWidth: 640, minWidth: 0 }}>
      <label htmlFor={id} style={labelStyle}>CC email</label>
      <div style={{ display: 'flex', alignItems: 'start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <ClientEmailAutocompleteInput
            id={id} mode="list" style={inputStyle} value={draft}
            onChange={next => { setDraft(next); setError(''); }}
            placeholder="Enter an email address, then Add"
            aria-invalid={Boolean(error)} aria-describedby={error ? `${id}-error` : undefined}
            onKeyDown={event => {
              if (event.key === 'Enter') { event.preventDefault(); add(); }
            }}
          />
        </div>
        <button type="button" onClick={add} disabled={!draft.trim()}
          style={{ padding: '12px 18px', border: '1px solid #3156a3', borderRadius: 10, background: '#eef4ff', color: '#234585', fontWeight: 600, cursor: draft.trim() ? 'pointer' : 'default' }}>
          Add
        </button>
      </div>
      {error ? <div id={`${id}-error`} role="alert" style={{ color: '#b42318', fontSize: 12 }}>{error}</div> : null}
      {emails.length > 0 ? (
        <ul aria-label="Added CC addresses" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: 0, padding: 0, listStyle: 'none' }}>
          {emails.map(email => (
            <li key={email} style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: '100%', background: '#eef4ff', borderRadius: 8, padding: '6px 10px' }}>
              <span style={{ overflowWrap: 'anywhere' }}>{email}</span>
              <button type="button" aria-label={`Remove ${email}`} onClick={() => onChange(emails.filter(item => item !== email).join(', '))}
                style={{ border: 0, background: 'transparent', color: '#234585', cursor: 'pointer', padding: 4 }}>×</button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
